import * as Sentry from '@sentry/react';
import { signInWithRedirect } from 'aws-amplify/auth';

import { REACT_APP_BYPASS, SSO_CUSTOM_PROVIDER } from '#shared';

// The typed error `fetchWithAuthHeaders` throws when a mid-session 401/403 could
// not be recovered by a forced token refresh. Matched by name (not `instanceof`)
// to avoid an import cycle between the auth and api layers.
//
// We deliberately do NOT react to AuthTokenUnavailableError (no token up front):
// that fires on the normal pre-login load, where AuthProvider already runs the
// sign-in flow, so reacting here would race it and pollute auth telemetry.
const SESSION_LOST_ERROR_NAMES = new Set(['SessionExpiredError']);

// Coalesces concurrent failing requests so they don't each fire a redirect
// (a storm). Set before the await; a successful redirect navigates away (a fresh
// load resets it), and on a failed redirect we clear it in the catch so a later
// genuine session loss can try again.
let reauthAttempted = false;

const isSessionLostError = (error: unknown): boolean => {
  const name = (error as { name?: unknown } | null | undefined)?.name;
  return typeof name === 'string' && SESSION_LOST_ERROR_NAMES.has(name);
};

/**
 * Central, loop-guarded reaction to a lost auth session. Called from the
 * data-layer error handlers (RTK base query + TanStack QueryCache.onError).
 *
 * Fires only for `SessionExpiredError` (a mid-session 401/403 that a forced
 * token refresh could not recover). It re-initiates the login flow so the board
 * recovers instead of sitting frozen until a manual reload.
 */
export const handleSessionExpired = async (error: unknown): Promise<void> => {
  if (REACT_APP_BYPASS === 'true') {
    return;
  }
  if (!isSessionLostError(error)) {
    return;
  }
  if (reauthAttempted) {
    return;
  }
  reauthAttempted = true;

  try {
    await signInWithRedirect({ provider: { custom: SSO_CUSTOM_PROVIDER } });
  } catch (reauthError) {
    // The re-auth redirect itself failed (e.g. InvalidOriginException, a
    // deterministic origin/config error that a reload will NOT fix). Report it
    // so the broken recovery path is visible, and clear the guard so a later
    // session-loss can retry (a config error will recur, but a transient one
    // may succeed).
    reauthAttempted = false;
    Sentry.captureException(reauthError, {
      tags: {
        category: 'auth',
        ['auth_error']: 'reauth_failed',
      },
    });
  }
};
